/**
 * EdgePropertyPopover - Inline editor for edge properties
 *
 * Shows a small popover when clicking an edge label, allowing users to:
 * - Edit the voice trigger phrase
 * - Change transition style (for navigation edges)
 * - Configure input/output mappings (for agent/data edges)
 *
 * This is a simple inline approach - click label, edit, click away to close.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { AppEdgeData, AppEdgeType, AppWidget } from '@deep-agents/shared-types';

interface EdgePropertyPopoverProps {
  /** Current edge data */
  data: AppEdgeData;
  /** Called when edge data changes */
  onUpdate: (data: AppEdgeData) => void;
  /** Called when popover should close */
  onClose: () => void;
  /** Position for the popover */
  position: { x: number; y: number };
  /** Button widgets from the source node (for linking a button to this edge) */
  sourceButtons?: AppWidget[];
}

export function EdgePropertyPopover({
  data,
  onUpdate,
  onClose,
  position,
  sourceButtons = [],
}: EdgePropertyPopoverProps) {
  // Transition type for navigation edges
  type TransitionType = 'slide' | 'fade' | 'none';

  // Local state for editing
  const [triggerPhrase, setTriggerPhrase] = useState(
    data.type === 'navigation' || data.type === 'agent-trigger'
      ? data.triggerPhrase || ''
      : ''
  );
  const [transition, setTransition] = useState<TransitionType>(
    data.type === 'navigation' ? data.transition || 'slide' : 'slide'
  );
  const [sourceWidgetId, setSourceWidgetId] = useState<string>(
    data.type === 'navigation' ? data.sourceWidgetId || '' : ''
  );

  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // NOTE: No click-outside-to-close handler.
  // The popover is rendered inside an EdgeLabelRenderer portal,
  // which makes DOM containment checks unreliable.
  // Users close via the X button or Save button instead.

  // Save and close
  const handleSave = useCallback(() => {
    let updatedData: AppEdgeData;

    if (data.type === 'navigation') {
      updatedData = {
        ...data,
        triggerPhrase: triggerPhrase || undefined,
        transition: transition as 'slide' | 'fade' | 'none',
        sourceWidgetId: sourceWidgetId || undefined,
      };
    } else if (data.type === 'agent-trigger') {
      updatedData = {
        ...data,
        triggerPhrase: triggerPhrase || undefined,
      };
    } else {
      // Data binding - no trigger phrase to edit
      updatedData = data;
    }

    onUpdate(updatedData);
    onClose();
  }, [data, triggerPhrase, transition, sourceWidgetId, onUpdate, onClose]);

  // Handle Enter key to save
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        onClose();
      }
    },
    [handleSave, onClose]
  );

  // Get title based on edge type
  const getTitle = (): string => {
    switch (data.type) {
      case 'navigation':
        return 'Navigation';
      case 'agent-trigger':
        return 'Agent Trigger';
      case 'data-binding':
        return 'Data Binding';
      default:
        return 'Edge';
    }
  };

  return (
    <div
      ref={popoverRef}
      data-edge-popover
      className="nodrag nopan nowheel pointer-events-auto fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-3 min-w-[240px]"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -100%) translateY(-8px)',
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
          {getTitle()}
        </span>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-lg leading-none"
        >
          x
        </button>
      </div>

      {/* Voice Trigger (for navigation and agent-trigger) */}
      {(data.type === 'navigation' || data.type === 'agent-trigger') && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Voice Trigger Phrase
          </label>
          <input
            ref={inputRef}
            type="text"
            value={triggerPhrase}
            onChange={(e) => setTriggerPhrase(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={(e) => e.stopPropagation()}
            placeholder="e.g., go to settings"
            className="nodrag nopan w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          />
          <p className="text-[10px] text-gray-400 mt-1">
            Users can say this to trigger the action
          </p>
        </div>
      )}

      {/* Transition style (for navigation only) */}
      {data.type === 'navigation' && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Transition
          </label>
          <select
            value={transition}
            onChange={(e) => setTransition(e.target.value as TransitionType)}
            className="nodrag nopan w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="slide">Slide</option>
            <option value="fade">Fade</option>
            <option value="none">None</option>
          </select>
        </div>
      )}

      {/* Linked Button selector (for navigation edges with buttons on source screen) */}
      {data.type === 'navigation' && sourceButtons.length > 0 && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Linked Button
          </label>
          <select
            value={sourceWidgetId}
            onChange={(e) => setSourceWidgetId(e.target.value)}
            className="nodrag nopan w-full px-2 py-1.5 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="">None (screen-level)</option>
            {sourceButtons.map((btn) => (
              <option key={btn.id} value={btn.id}>
                {btn.props && 'label' in btn.props ? (btn.props as { label?: string }).label || 'Button' : 'Button'}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-gray-400 mt-1">
            Tapping this button will trigger navigation
          </p>
        </div>
      )}

      {/* Data binding info (read-only for now) */}
      {data.type === 'data-binding' && (
        <div className="text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs bg-gray-100 px-1 rounded">
              {data.sourceField}
            </span>
            <span className="text-gray-400">to</span>
            <span className="font-mono text-xs bg-gray-100 px-1 rounded">
              {data.targetField}
            </span>
          </div>
        </div>
      )}

      {/* Save button */}
      <button
        onClick={handleSave}
        className="w-full mt-2 px-3 py-1.5 bg-violet-600 text-white text-sm font-medium rounded hover:bg-violet-700 transition-colors"
      >
        Save
      </button>
    </div>
  );
}
